import type { ChaoUcStageScheduleDto } from "@/services/chaoDeObra";

export function parseIsoLocal(iso?: string | null): Date | null {
  const s = String(iso ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function inclusiveDays(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.max(0, Math.round((b - a) / 86_400_000)) + 1;
}

export function pluralDias(n: number): string {
  return n === 1 ? "1 dia" : `${n} dias`;
}

/** Sem data final: em construção até hoje. Com data final: duração total da etapa. */
export function formatStageDurationMessage(
  schedule?: ChaoUcStageScheduleDto | null,
  today = new Date(),
): string | null {
  const start = parseIsoLocal(schedule?.startDate);
  if (!start) return null;
  const end = parseIsoLocal(schedule?.endDate);
  if (end) return `Duração total da obra: ${pluralDias(inclusiveDays(start, end))}`;
  return `Em construção há ${pluralDias(inclusiveDays(start, today))}`;
}

function toDayNumber(d: Date): number {
  return Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / 86_400_000);
}

/** Une intervalos inclusivos e conta os dias únicos (etapas em paralelo não somam duas vezes). */
export function mergeIntervalDays(intervals: { start: Date; end: Date }[]): number {
  if (!intervals.length) return 0;
  const sorted = intervals
    .map((iv) => {
      const a = toDayNumber(iv.start);
      const b = toDayNumber(iv.end);
      return a <= b ? { a, b } : { a: b, b: a };
    })
    .sort((x, y) => x.a - y.a || x.b - y.b);

  let total = 0;
  let curA = sorted[0]!.a;
  let curB = sorted[0]!.b;
  for (let i = 1; i < sorted.length; i++) {
    const { a, b } = sorted[i]!;
    if (a <= curB + 1) {
      curB = Math.max(curB, b);
    } else {
      total += curB - curA + 1;
      curA = a;
      curB = b;
    }
  }
  return total + (curB - curA + 1);
}

export function formatObraDurationMessage(
  schedules: Record<string, ChaoUcStageScheduleDto>,
  today = new Date(),
): string | null {
  const intervals: { start: Date; end: Date }[] = [];
  let anyOpen = false;

  for (const schedule of Object.values(schedules)) {
    const start = parseIsoLocal(schedule.startDate);
    if (!start) continue;
    const end = parseIsoLocal(schedule.endDate);
    if (end) {
      intervals.push({ start, end });
    } else {
      anyOpen = true;
      intervals.push({ start, end: today });
    }
  }

  if (!intervals.length) return null;
  const days = mergeIntervalDays(intervals);
  if (anyOpen) return `Em construção há ${pluralDias(days)}`;
  return `Duração total da obra: ${pluralDias(days)}`;
}
